import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/FriendList.css';
import { useFriends } from '../hooks/useFriends';

const FriendList = ({ onClose, isOwnProfile = false, userNumber = null }) => {
    const navigate = useNavigate();
    const {
        getFriends,
        getFriendsByUserNumber,
        getSentRequests,
        getReceivedRequests,
        acceptFriendRequest,
        declineFriendRequest,
        cancelFriendRequest,
        removeFriend
    } = useFriends();

    const [friends, setFriends] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('friends');
    const [actionLoading, setActionLoading] = useState(null);

    useEffect(() => {
        fetchAllFriendData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwnProfile, userNumber]);

    useEffect(() => {
        // Reset to friends tab when viewing other user's profile
        if (!isOwnProfile) {
            setActiveTab('friends');
        }
    }, [isOwnProfile]);

    const fetchAllFriendData = async () => {
        setLoading(true);
        try {
            const promises = [];

            // Get friends - use different endpoint based on whose profile we're viewing
            if (isOwnProfile) {
                promises.push(getFriends());
            } else if (userNumber) {
                promises.push(getFriendsByUserNumber(userNumber));
            } else {
                promises.push(getFriends()); // fallback to own friends
            }

            // Only fetch sent and received requests for own profile
            if (isOwnProfile) {
                promises.push(getSentRequests(), getReceivedRequests());
            }

            const results = await Promise.all(promises);

            setFriends(results[0] || []);

            if (isOwnProfile) {
                setSentRequests(results[1] || []);
                setReceivedRequests(results[2] || []);
            } else {
                setSentRequests([]);
                setReceivedRequests([]);
            }
        } catch (error) {
            console.error('Error fetching friend data');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptRequest = async (requesterId) => {
        setActionLoading(requesterId);
        try {
            await acceptFriendRequest(requesterId);
            await fetchAllFriendData(); // Refresh data
        } catch (error) {
            console.error('Error accepting friend request');
        } finally {
            setActionLoading(null);
        }
    };

    const handleDeclineRequest = async (requesterId) => {
        setActionLoading(requesterId);
        try {
            await declineFriendRequest(requesterId);
            await fetchAllFriendData(); // Refresh data
        } catch (error) {
            console.error('Error declining friend request');
        } finally {
            setActionLoading(null);
        }
    };

    const handleCancelRequest = async (recipientId) => {
        setActionLoading(recipientId);
        try {
            await cancelFriendRequest(recipientId);
            await fetchAllFriendData(); // Refresh data
        } catch (error) {
            console.error('Error cancelling friend request');
        } finally {
            setActionLoading(null);
        }
    };

    const handleRemoveFriend = async (friendId) => {
        if (window.confirm('Are you sure you want to remove this friend?')) {
            setActionLoading(friendId);
            try {
                await removeFriend(friendId);
                await fetchAllFriendData(); // Refresh data
            } catch (error) {
                console.error('Error removing friend');
            } finally {
                setActionLoading(null);
            }
        }
    };

    const navigateToProfile = (userNumber) => {
        navigate(`/profile/${userNumber}`);
        onClose();
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const renderTabContent = () => {
        if (loading) {
            return (
                <div className="friend-list-loading">
                    <div className="loader" />
                </div>
            );
        }

        switch (activeTab) {
            case 'friends':
                return (
                    <div className="friend-list-content">
                        {friends.length === 0 ? (
                            <div className="no-friends-message">
                                <p>No friends yet</p>
                                <p>Add friends to see them here!</p>
                            </div>
                        ) : (
                            friends.map((friend) => (
                                <div key={friend.friend._id} className="friend-entry-container" onClick={() => navigateToProfile(friend.friend.userNumber)}>
                                    <div className="friend-info">
                                        <p
                                            className="friend-username"
                                        >
                                            {friend.friend.username}
                                        </p>
                                        <p className="friend-date">
                                            Friends since {formatDate(friend.friendedAt)}
                                        </p>
                                    </div>
                                    {isOwnProfile && (
                                        <div className="friend-actions">
                                            <button
                                                className="friend-action-btn remove-btn"
                                                onClick={() => handleRemoveFriend(friend.friend._id)}
                                                disabled={actionLoading === friend.friend._id}
                                            >
                                                {actionLoading === friend.friend._id ? <div className="loader-small" /> : 'Remove'}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                );

            case 'received':
                return (
                    <div className="friend-list-content">
                        {receivedRequests.length === 0 ? (
                            <div className="no-friends-message">
                                <p>No pending requests</p>
                            </div>
                        ) : (
                            receivedRequests.map((request) => (
                                <div key={request.requester._id} className="friend-entry-container">
                                    <div className="friend-info">
                                        <p
                                            className="friend-username"
                                            onClick={() => navigateToProfile(request.requester.userNumber)}
                                        >
                                            {request.requester.username}
                                        </p>
                                        <p className="friend-date">
                                            Sent {formatDate(request.receivedAt)}
                                        </p>
                                    </div>
                                    <div className="friend-actions">
                                        <button
                                            className="friend-action-btn accept-btn"
                                            onClick={() => handleAcceptRequest(request.requester._id)}
                                            disabled={actionLoading === request.requester._id}
                                        >
                                            {actionLoading === request.requester._id ? <div className="loader-small" /> : 'Accept'}
                                        </button>
                                        <button
                                            className="friend-action-btn decline-btn"
                                            onClick={() => handleDeclineRequest(request.requester._id)}
                                            disabled={actionLoading === request.requester._id}
                                        >
                                            Decline
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );

            case 'sent':
                return (
                    <div className="friend-list-content">
                        {sentRequests.length === 0 ? (
                            <div className="no-friends-message">
                                <p>No pending requests</p>
                            </div>
                        ) : (
                            sentRequests.map((request) => (
                                <div key={request.recipient._id} className="friend-entry-container">
                                    <div className="friend-info">
                                        <p
                                            className="friend-username"
                                            onClick={() => navigateToProfile(request.recipient.userNumber)}
                                        >
                                            {request.recipient.username}
                                        </p>
                                        <p className="friend-date">
                                            Sent {formatDate(request.sentAt)}
                                        </p>
                                    </div>
                                    <div className="friend-actions">
                                        <button
                                            className="friend-action-btn cancel-btn"
                                            onClick={() => handleCancelRequest(request.recipient._id)}
                                            disabled={actionLoading === request.recipient._id}
                                        >
                                            {actionLoading === request.recipient._id ? <div className="loader-small" /> : 'Cancel'}
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="overlay" onClick={onClose}>
            <div className="friend-list-container" onClick={(e) => e.stopPropagation()}>
                <div className="friend-list-header">
                    <h1 className="friend-list-title">Friends</h1>

                    <div className="view-button-container">
                        <div className="col-auto">
                            <button
                                className={`view-button ${activeTab === 'friends' ? 'active' : ''}`}
                                onClick={() => setActiveTab('friends')}
                            >
                                Friends ({friends.length})
                            </button>
                        </div>
                        {isOwnProfile && (
                            <>
                                <div className="col-auto">
                                    <button
                                        className={`view-button ${activeTab === 'received' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('received')}
                                    >
                                        Received ({receivedRequests.length})
                                    </button>
                                </div>
                                <div className="col-auto">
                                    <button
                                        className={`view-button ${activeTab === 'sent' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('sent')}
                                    >
                                        Sent ({sentRequests.length})
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <button className="overlay-close" onClick={onClose}>X</button>
                </div>

                <div className="friend-list-scroll-container">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
};

export default FriendList;
